import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { seedSelectedLogo } from '../api/get-logos';
import { TLogo } from '../types';

import { useLogoPicker } from './use-logo-picker';

type LogoFormFields = { logo_id: string | null; entity_label?: string };

type UseLogoFormPickerParams<TForm extends LogoFormFields> = {
  form: UseFormReturn<TForm>;
  /** The label stored on the presence being edited. */
  fallbackLabel?: string | null;
  labelsIn?: string[];
  /** Whether the form is the one on screen, so it owns the presence label. */
  isActive: boolean;
};

/**
 * The logo picker of a presence form: names the chosen logo, keeps `entity_label` in step with
 * it, and selects a logo created from the picker.
 */
export function useLogoFormPicker<TForm extends LogoFormFields>({
  form,
  fallbackLabel,
  labelsIn,
  isActive,
}: UseLogoFormPickerParams<TForm>) {
  const queryClient = useQueryClient();
  // The generic form only guarantees the two fields this hook reads and writes.
  const fields = form as unknown as UseFormReturn<LogoFormFields>;
  const logoId = fields.watch('logo_id');
  const picker = useLogoPicker({ value: logoId, fallbackLabel, labelsIn });

  const selectedLogo = picker.findOption(logoId)?.entity ?? null;
  const selectedName = selectedLogo?.name ?? '';

  // RISK: written only once the value is settled. While a new presence's logo is being named,
  // writing would replace its label with an empty one.
  useEffect(() => {
    if (!isActive || !logoId || picker.isValueResolving) return;
    fields.setValue('entity_label', selectedName);
  }, [isActive, logoId, picker.isValueResolving, selectedName, fields]);

  const selectCreatedLogo = useCallback(
    (logo: TLogo) => {
      seedSelectedLogo(queryClient, logo);
      fields.setValue('logo_id', logo.id, { shouldValidate: true });
    },
    [queryClient, fields]
  );

  return { picker, selectedLogo, selectCreatedLogo };
}
